import { Box, LinearProgress, Typography, linearProgressClasses, useTheme } from "@mui/material";
import { Macros, getCaloriesPerMacro } from "@notemeal/shared-utils-macro-protocol";
import { useId } from "react";

interface MacrosColorBarProps {
  macros: Macros;
  showKcals: boolean;
}

export const MacrosColorBar = ({ macros, showKcals }: MacrosColorBarProps) => {
  const {
    palette: { greyscale, cho, pro, fat },
  } = useTheme();
  const { cho: choCal, pro: proCal, fat: fatCal } = getCaloriesPerMacro(macros);
  const total = macros.kcal;
  const choBar = Math.round((choCal / total) * 100) + "%";
  const proBar = Math.round((proCal / total) * 100) + "%";
  const fatBar = Math.round((fatCal / total) * 100) + "%";
  const labelId = useId();
  // Only the first macro segment with calories carries the accessible progressbar semantics;
  // the rest are purely decorative continuations of the same overall value.
  const firstSegmentWithCalories = choCal > 0 ? "cho" : proCal > 0 ? "pro" : fatCal > 0 ? "fat" : null;
  const macroBreakdownText = `${Math.round(choCal)} kcal carbs, ${Math.round(proCal)} kcal protein, ${Math.round(fatCal)} kcal fat`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography id={labelId} noWrap variant="subtitle1">
          Calories from Macros
        </Typography>
        {showKcals && <Typography variant="subtitle1">{`${Math.round(total)}kcal`}</Typography>}
      </Box>
      {total === 0 && (
        <LinearProgress
          sx={{
            [`& .${linearProgressClasses.bar}`]: {
              backgroundColor: greyscale[300]
            },
          }}
          variant="determinate"
          value={100}
          aria-labelledby={labelId}
        />
      )}
      <Box sx={{ display: "flex" }}>
        {choCal > 0 && (
          <LinearProgress
            sx={{
              width: choBar,
              [`& .${linearProgressClasses.bar}`]: {
                backgroundColor: cho.main,
              },
            }}
            variant="determinate"
            value={100}
            aria-labelledby={firstSegmentWithCalories === "cho" ? labelId : undefined}
            aria-valuetext={firstSegmentWithCalories === "cho" ? macroBreakdownText : undefined}
            role={firstSegmentWithCalories === "cho" ? undefined : "presentation"}
            aria-hidden={firstSegmentWithCalories === "cho" ? undefined : "true"}
          />
        )}
        {proCal > 0 && (
          <LinearProgress
            sx={{
              width: proBar,
              [`& .${linearProgressClasses.bar}`]: {
                backgroundColor: pro.main,
              },
            }}
            variant="determinate"
            value={100}
            aria-labelledby={firstSegmentWithCalories === "pro" ? labelId : undefined}
            aria-valuetext={firstSegmentWithCalories === "pro" ? macroBreakdownText : undefined}
            role={firstSegmentWithCalories === "pro" ? undefined : "presentation"}
            aria-hidden={firstSegmentWithCalories === "pro" ? undefined : "true"}
          />
        )}
        {fatCal > 0 && (
          <LinearProgress
            sx={{
              width: fatBar,
              [`& .${linearProgressClasses.bar}`]: {
                backgroundColor: fat.main,
              },
            }}
            variant="determinate"
            value={100}
            aria-labelledby={firstSegmentWithCalories === "fat" ? labelId : undefined}
            aria-valuetext={firstSegmentWithCalories === "fat" ? macroBreakdownText : undefined}
            role={firstSegmentWithCalories === "fat" ? undefined : "presentation"}
            aria-hidden={firstSegmentWithCalories === "fat" ? undefined : "true"}
          />
        )}
      </Box>
    </Box>
  );
};
